import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import type { ArcheDisplayMeta } from '@/lib/api';

interface ArcheChipProps {
  /** The tagged arche id, or null for a Fias system / platform issue. */
  archeId: string | null | undefined;
  /** Resolved display meta (from useArcheDisplayMap); absent → "Unknown arche". */
  meta?: ArcheDisplayMeta;
  className?: string;
}

/**
 * Compact indicator of what a support submission concerns: a specific arche
 * (icon + name) or a "Fias system" pill when untagged/null. A tagged-but-
 * unresolvable arche (deleted/private) degrades to a neutral "Unknown arche"
 * chip rather than erroring.
 */
export function ArcheChip({ archeId, meta, className }: ArcheChipProps) {
  if (!archeId) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground',
          className,
        )}
      >
        <Sparkles className="h-3 w-3" />
        Fias system
      </span>
    );
  }

  const imageRef = meta?.imageRef;
  return (
    <span
      className={cn(
        'inline-flex max-w-[180px] items-center gap-1.5 rounded-full border border-border bg-background px-2 py-0.5 text-xs',
        className,
      )}
      title={meta?.name ?? archeId}
    >
      {imageRef ? (
        <ArcheIconImg imageRef={imageRef} alt="" size={64} className="h-4 w-4 rounded-sm" />
      ) : (
        <DefaultArcheIcon className="h-4 w-4 rounded-sm" />
      )}
      <span className="truncate">{meta?.name ?? 'Unknown arche'}</span>
    </span>
  );
}
